// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/dashboard/widgets.tsx
//  Widgets de dados da Fase 2 (serviços/financeiro/vendas/fiscal/CRM).
//  Todos seguem o mesmo esqueleto: WidgetCard + useQuery no endpoint
//  agregado correspondente (staleTime 60s). Cards compactos, dados enxutos.
// ════════════════════════════════════════════════════════════════════════════

import { useState, type ComponentType, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock, FileCheck2, FileSignature, Filter,
  TrendingDown, TrendingUp, Wallet, Wrench,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { widgetsService } from "./services/widgetsService";
import type { WidgetProps } from "./types";

const fmtMoeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtHora = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// ── Esqueleto compartilhado ───────────────────────────────────────────────

function WidgetCard({
  titulo, descricao, icone: Icone, carregando, children, acao,
}: {
  titulo: string;
  descricao: string;
  icone: ComponentType<{ className?: string }>;
  carregando: boolean;
  children: ReactNode;
  /** Controle opcional no cabeçalho (ex.: seletor de período). */
  acao?: ReactNode;
}) {
  return (
    <Card className="shadow-card border-border/50 h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-heading text-base">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
            <Icone className="h-4 w-4 text-primary" />
          </div>
          <span className="flex-1">{titulo}</span>
          {acao}
        </CardTitle>
        <CardDescription className="text-xs">{descricao}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {carregando ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        ) : children}
      </CardContent>
    </Card>
  );
}

function Stat({ rotulo, valor, destaque }: { rotulo: string; valor: string; destaque?: "positivo" | "negativo" }) {
  const cor =
    destaque === "positivo" ? "text-success" :
    destaque === "negativo" ? "text-destructive" : "text-foreground";
  return (
    <div className="rounded-lg bg-muted/40 border border-border/30 p-2.5">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{rotulo}</p>
      <p className={`text-sm font-semibold font-heading ${cor}`}>{valor}</p>
    </div>
  );
}

// ── Financeiro ────────────────────────────────────────────────────────────

export function ContasVencerWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-contas-vencer"],
    queryFn: widgetsService.contasVencer,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Contas a Vencer" descricao="Títulos em aberto — hoje e próximos 7 dias" icone={CalendarClock} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <Stat rotulo={`Receber hoje (${data.receberHoje.qtd})`} valor={fmtMoeda(data.receberHoje.valor)} destaque="positivo" />
          <Stat rotulo={`Pagar hoje (${data.pagarHoje.qtd})`} valor={fmtMoeda(data.pagarHoje.valor)} destaque="negativo" />
          <Stat rotulo={`Receber 7 dias (${data.receberSeteDias.qtd})`} valor={fmtMoeda(data.receberSeteDias.valor)} />
          <Stat rotulo={`Pagar 7 dias (${data.pagarSeteDias.qtd})`} valor={fmtMoeda(data.pagarSeteDias.valor)} />
        </div>
      )}
    </WidgetCard>
  );
}

export function InadimplenciaWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-inadimplencia"],
    queryFn: widgetsService.inadimplencia,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Inadimplência" descricao="Recebíveis vencidos e % da carteira" icone={TrendingDown} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <Stat rotulo={`Vencidos (${data.qtdTitulosVencidos})`} valor={fmtMoeda(data.valorVencido)} destaque="negativo" />
          <Stat rotulo="% da carteira" valor={`${data.percentualCarteira.toLocaleString("pt-BR")}%`} destaque={data.percentualCarteira > 0 ? "negativo" : undefined} />
          <div className="col-span-2">
            <Stat rotulo="Carteira em aberto" valor={fmtMoeda(data.carteiraAberta)} />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

export function FluxoProjetadoWidget({ config, onConfigChange }: WidgetProps = {}) {
  // Config por instância (Fase 4): horizonte em dias. No modo edição a escolha
  // é persistida junto com o layout; fora dele vale só para a sessão.
  const [diasLocal, setDiasLocal] = useState<number | null>(null);
  const dias = diasLocal ?? (Number(config?.dias) || 30);

  const { data, isLoading } = useQuery({
    queryKey: ["dw-fluxo-projetado", dias],
    queryFn: () => widgetsService.fluxoProjetado(dias),
    staleTime: 60_000,
  });

  const seletor = (
    <div className="widget-no-drag">
      <Select
        value={String(dias)}
        onValueChange={(v) => {
          setDiasLocal(Number(v));
          onConfigChange?.({ ...config, dias: Number(v) });
        }}
      >
        <SelectTrigger className="h-7 w-24 text-xs font-normal">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="30">30 dias</SelectItem>
          <SelectItem value="60">60 dias</SelectItem>
          <SelectItem value="90">90 dias</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <WidgetCard titulo="Fluxo Projetado" descricao={`Previsto para os próximos ${dias} dias`} icone={Wallet} carregando={isLoading} acao={seletor}>
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <Stat rotulo="Entradas" valor={fmtMoeda(data.entradasPrevistas)} destaque="positivo" />
          <Stat rotulo="Saídas" valor={fmtMoeda(data.saidasPrevistas)} destaque="negativo" />
          <div className="col-span-2">
            <Stat rotulo="Saldo projetado" valor={fmtMoeda(data.saldoProjetado)} destaque={data.saldoProjetado >= 0 ? "positivo" : "negativo"} />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

// ── Serviços / contratos / agenda ─────────────────────────────────────────

export function ContratosWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-contratos"],
    queryFn: widgetsService.contratosResumo,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Contratos" descricao="Carteira de contratos ativos" icone={FileSignature} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <Stat rotulo="Ativos" valor={String(data.ativos)} />
          <Stat rotulo="Vencem em 30 dias" valor={String(data.vencendoTrintaDias)} destaque={data.vencendoTrintaDias > 0 ? "negativo" : undefined} />
          <div className="col-span-2">
            <Stat rotulo="Valor total ativo" valor={fmtMoeda(data.valorTotalAtivos)} />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

export function AgendaHojeWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["dw-agenda-hoje"],
    queryFn: widgetsService.agendaHoje,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Agenda de Hoje" descricao="Seus compromissos do dia" icone={CalendarClock} carregando={isLoading}>
      {data.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Nenhum compromisso hoje.</p>
      ) : (
        <div className="space-y-2">
          {data.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg bg-muted/40 border border-border/30 p-2.5">
              <span className="text-xs font-bold font-heading text-primary shrink-0">{fmtHora(a.dtInicio)}</span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{a.titulo}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {a.tipo}{a.cliente ? ` · ${a.cliente}` : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

export function OsResumoWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-os-resumo"],
    queryFn: widgetsService.osResumo,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Ordens de Serviço" descricao="Situação das OS" icone={Wrench} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <Stat rotulo="Abertas" valor={String(data.abertas)} destaque={data.abertas > 0 ? "negativo" : undefined} />
          <Stat rotulo="No mês" valor={String(data.emitidasMes)} />
          <Stat rotulo="Concluídas" valor={String(data.concluidasMes)} destaque="positivo" />
        </div>
      )}
    </WidgetCard>
  );
}

// ── Vendas / fiscal / CRM ─────────────────────────────────────────────────

export function VendasDiaWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-vendas-dia"],
    queryFn: widgetsService.vendasResumoDia,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Vendas do Dia" descricao="DAVs emitidos hoje" icone={TrendingUp} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <Stat rotulo="Vendas" valor={String(data.qtdVendas)} />
          <Stat rotulo="Total" valor={fmtMoeda(data.totalLiquido)} destaque="positivo" />
          <Stat rotulo="Ticket médio" valor={fmtMoeda(data.ticketMedio)} />
        </div>
      )}
    </WidgetCard>
  );
}

export function FiscalEmissaoWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-fiscal-emissao"],
    queryFn: widgetsService.fiscalResumoEmissao,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Emissão Fiscal" descricao="Documentos do mês corrente" icone={FileCheck2} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <Stat rotulo={`NF-e (${data.nfeQtd})`} valor={fmtMoeda(data.nfeValor)} />
          <Stat rotulo={`NFC-e (${data.nfceQtd})`} valor={fmtMoeda(data.nfceValor)} />
          <div className="col-span-2">
            <Stat rotulo="Canceladas no mês" valor={String(data.canceladas)} destaque={data.canceladas > 0 ? "negativo" : undefined} />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

export function CrmPipelineWidget({ modoEdicao: _m = false }: { modoEdicao?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ["dw-crm-kpis"],
    queryFn: widgetsService.crmKpis,
    staleTime: 60_000,
  });
  return (
    <WidgetCard titulo="Pipeline CRM" descricao="Oportunidades em andamento" icone={Filter} carregando={isLoading}>
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <Stat rotulo={`Abertas (${data.oportunidadesAbertas})`} valor={fmtMoeda(data.valorPipelineAberto)} />
          <Stat rotulo="Conversão" valor={`${data.taxaConversao.toLocaleString("pt-BR")}%`} />
          <div className="col-span-2">
            <Stat rotulo="Ganho no mês" valor={fmtMoeda(data.valorGanhoMes)} destaque="positivo" />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}
